import { useCallback } from 'react';
import {
  useForm,
  useFormState,
  UseFormProps,
  UseFormReturn,
  FieldValues,
  SubmitHandler,
  SubmitErrorHandler,
} from 'react-hook-form';
import { pick } from 'lodash';

interface UseUpdateFormReturn<T extends FieldValues> extends UseFormReturn<T> {
  // Add a new method for dirty field submission while keeping handleSubmit compatible
  handleUpdateSubmit: (
    onValid: SubmitHandler<Partial<T>>,
    onInvalid?: SubmitErrorHandler<T>,
  ) => (e?: React.BaseSyntheticEvent) => Promise<void>;
}

/**
 * Recursively converts empty strings to null in a plain object.
 * Dirty fields with "" represent an intentional clear — APIs expect null, not "".
 */
function emptyStringsToNull<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      typeof value === 'object' && value !== null && !Array.isArray(value)
        ? emptyStringsToNull(value as Record<string, unknown>)
        : value === ''
          ? null
          : value,
    ]),
  ) as T;
}

/**
 * Enhanced useForm hook that extends React Hook Form with automatic dirty field tracking.
 * Uses React Hook Form's built-in dirtyFields and validation.
 * The handleSubmit function only passes changed fields to the submit handler,
 * with empty strings normalized to null for API compatibility.
 */
export const useUpdateForm = <T extends FieldValues>(
  options: UseFormProps<T>,
): UseUpdateFormReturn<T> => {
  const form = useForm<T>(options);
  const { dirtyFields } = useFormState({
    control: form.control,
  });

  /**
   * Enhanced handleUpdateSubmit that only passes dirty fields to the submit handler
   */
  const handleUpdateSubmit = useCallback(
    (onValid: SubmitHandler<Partial<T>>, onInvalid?: SubmitErrorHandler<T>) => {
      return form.handleSubmit(async (data: T) => {
        const dirtyFieldKeys = Object.keys(dirtyFields).filter(
          (key) => dirtyFields[key as keyof typeof dirtyFields],
        );

        const changedFields = pick(data, dirtyFieldKeys) as Partial<T>;
        const normalized = emptyStringsToNull(
          changedFields as Record<string, unknown>,
        ) as Partial<T>;

        await onValid(normalized);
      }, onInvalid);
    },
    [form, dirtyFields],
  );

  return {
    ...form,
    handleUpdateSubmit,
  };
};
